<template>
  <div class="ai-settings-page">
    <div class="toolbar">
      <div class="title-block">
        <h1>AI-настройки</h1>
        <p class="subtitle">
          Провайдер LLM, системный промпт и включённые возможности ассистента.
          Пустые поля не сохраняются; «Сбросить» у поля возвращает значение из окружения.
        </p>
      </div>
    </div>

    <div v-if="loading" class="hint">Загрузка…</div>
    <div v-else-if="loadError" class="error">{{ loadError }}</div>

    <template v-else>
      <div class="settings-layout">
      <div class="tabs tabs-side">
        <button
          v-for="tab in visibleTabs"
          :key="tab.id"
          class="tab"
          :class="{ active: activeTab === tab.id }"
          @click="switchTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>
      <div class="settings-col">
      <div v-if="actionNotice" class="notice">{{ actionNotice }}</div>
      <div v-if="actionError" class="error">{{ actionError }}</div>

      <!-- Провайдер -->
      <section v-if="activeTab === 'provider'" class="card">
        <header class="card-header">
          <h2>Провайдер LLM</h2>
          <span v-if="groups.provider.has_override" class="badge">переопределено</span>
          <div class="header-actions">
            <button class="btn" :disabled="testing || !canTest" @click="testProvider">
              {{ testing ? 'Проверка…' : 'Проверить соединение' }}
            </button>
            <button class="btn primary" :disabled="saving === 'provider'" @click="saveGroup('provider')">
              {{ saving === 'provider' ? 'Сохранение…' : 'Сохранить' }}
            </button>
          </div>
        </header>
        <div v-if="testResult" class="test-result" :class="testResult.ok ? 'ok' : 'fail'">
          <template v-if="testResult.ok">
            Соединение ОК · {{ testResult.model }} · {{ testResult.latency_ms }} мс
          </template>
          <template v-else>{{ testResult.error }}</template>
        </div>

        <div class="fields two-col">
          <label class="field">
            <span class="field-label">Base URL</span>
            <input v-model.trim="forms.provider.base_url" type="text" placeholder="https://api.openai.com/v1" />
          </label>
          <label class="field">
            <span class="field-label">Модель</span>
            <input v-model.trim="forms.provider.model" type="text" placeholder="gpt-4.1-mini" />
          </label>
          <label class="field">
            <span class="field-label">Temperature (0–2)</span>
            <input v-model.number="forms.provider.temperature" type="number" min="0" max="2" step="0.1" />
          </label>
          <label class="field">
            <span class="field-label">Max tokens</span>
            <input v-model.number="forms.provider.max_tokens" type="number" min="256" max="128000" step="256" />
          </label>
          <label class="field">
            <span class="field-label">HTTP-прокси</span>
            <input v-model.trim="forms.provider.http_proxy" type="text" placeholder="http://singbox-proxy…" />
          </label>
          <label class="field">
            <span class="field-label">Таймаут запроса, сек</span>
            <input v-model.number="forms.provider.request_timeout_sec" type="number" min="5" max="600" step="5" />
          </label>
        </div>

        <div class="fields">
          <label class="field">
            <span class="field-label">
              API-ключ
              <span class="field-hint">
                {{ apiKeyHint }}
              </span>
            </span>
            <div class="api-key-row">
              <input v-model="forms.provider.api_key" type="password" autocomplete="new-password"
                     placeholder="оставьте пустым, чтобы не менять" />
              <button class="btn" :disabled="saving === 'provider'" @click="clearApiKey">
                Сбросить
              </button>
            </div>
          </label>
        </div>
      </section>

      <!-- Промпты -->
      <section v-if="activeTab === 'prompts'" class="card">
        <header class="card-header">
          <h2>Системный промпт</h2>
          <span v-if="groups.prompts.has_override" class="badge">переопределено</span>
          <div class="header-actions">
            <button class="btn primary" :disabled="saving === 'prompts'" @click="saveGroup('prompts')">
              {{ saving === 'prompts' ? 'Сохранение…' : 'Сохранить' }}
            </button>
          </div>
        </header>
        <p class="field-hint">
          Очистка поля и сохранение возвращают промпт по умолчанию из окружения.
        </p>
        <label class="field">
          <textarea v-model="forms.prompts.system_prompt" rows="14" spellcheck="false"></textarea>
        </label>
      </section>

      <!-- Фичи -->
      <section v-if="activeTab === 'features'" class="card">
        <header class="card-header">
          <h2>Возможности</h2>
          <span v-if="groups.features.has_override" class="badge">переопределено</span>
          <div class="header-actions">
            <button class="btn primary" :disabled="saving === 'features'" @click="saveGroup('features')">
              {{ saving === 'features' ? 'Сохранение…' : 'Сохранить' }}
            </button>
          </div>
        </header>
        <div class="switches">
          <label class="switch-row">
            <input v-model="forms.features.enabled" type="checkbox" />
            <span class="switch-label">AI-ассистент включён</span>
          </label>
          <label class="switch-row">
            <input v-model="forms.features.reports_enabled" type="checkbox" />
            <span class="switch-label">Отчёты (сохранение/повтор/обновление выводов)</span>
          </label>
          <label class="switch-row">
            <input v-model="forms.features.execute_sql_enabled" type="checkbox" />
            <span class="switch-label">execute_sql для агента (произвольный SQL через валидацию)</span>
          </label>
          <label class="switch-row">
            <input v-model="forms.features.memory_enabled" type="checkbox" />
            <span class="switch-label">Память (база знаний + личные заметки)</span>
          </label>
          <label class="switch-row">
            <input v-model="forms.features.diag_enabled" type="checkbox" />
            <span class="switch-label">Диагностика рейсов/TMS/ATI (read-only тулы агента)</span>
          </label>
        </div>
      </section>

      <!-- Профили агентов -->
      <section v-if="activeTab === 'profiles'" class="card">
        <header class="card-header">
          <h2>Профили агентов</h2>
          <div class="header-actions">
            <button class="btn primary" :disabled="profileSaving || !!profileForm" @click="startCreateProfile">
              Новый профиль
            </button>
          </div>
        </header>
        <p class="field-hint">
          Пресеты поведения ассистента: подмешиваются к системному промпту и определяют доступные
          инструменты. Встроенные профили можно править (промпт/описание) и отключать, но не удалять.
        </p>

        <div v-if="profileForm" class="profile-editor">
          <div class="fields two-col">
            <label class="field">
              <span class="field-label">Название</span>
              <input v-model.trim="profileForm.name" type="text" maxlength="100"
                     :disabled="profileForm.is_builtin" />
            </label>
            <label class="field">
              <span class="field-label">Описание (кратко, для людей)</span>
              <input v-model.trim="profileForm.description" type="text" maxlength="500" />
            </label>
          </div>
          <div class="field">
            <span class="field-label">Инструменты</span>
            <div v-if="profileForm.is_builtin" class="field-hint">
              У встроенного профиля набор инструментов фиксирован: {{ profileForm.toolsets.map(toolsetLabel).join(', ') }}
            </div>
            <div v-else class="switches">
              <label v-for="ts in toolsets" :key="ts.id" class="switch-row">
                <input type="checkbox" :checked="profileForm.toolsets.includes(ts.id)"
                       @change="toggleProfileToolset(ts.id)" />
                <span class="switch-label">{{ ts.label }}</span>
              </label>
            </div>
          </div>
          <label class="field">
            <span class="field-label">Поведенческий промпт (добавляется к системному)</span>
            <textarea v-model="profileForm.prompt" rows="10" spellcheck="false"
                      maxlength="20000" placeholder="Как ассистент должен себя вести в этом режиме…"></textarea>
          </label>
          <div class="profile-editor-actions">
            <button class="btn primary" :disabled="profileSaving" @click="saveProfile">
              {{ profileSaving ? 'Сохранение…' : 'Сохранить' }}
            </button>
            <button class="btn" :disabled="profileSaving" @click="cancelProfileEdit">Отмена</button>
          </div>
        </div>

        <div v-if="!profilesLoaded" class="hint">Загрузка…</div>
        <div v-else-if="!profiles.length" class="hint">Профилей пока нет.</div>
        <ul v-else class="profile-list">
          <li v-for="p in profiles" :key="p.id" class="profile-item" :class="{ inactive: p.is_active === false }">
            <div class="profile-item-main">
              <div class="profile-item-title">
                <strong>{{ p.name }}</strong>
                <span v-if="p.is_builtin" class="badge">встроенный</span>
                <span v-if="p.is_active === false" class="badge badge-off">отключён</span>
              </div>
              <div v-if="p.description" class="profile-item-desc">{{ p.description }}</div>
              <div class="profile-item-tools">
                <span v-for="ts in p.toolsets" :key="ts" class="tool-chip">{{ toolsetLabel(ts) }}</span>
              </div>
            </div>
            <div class="profile-item-actions">
              <button class="btn" :disabled="profileSaving" @click="startEditProfile(p)">Изменить</button>
              <button class="btn" :disabled="profileSaving" @click="toggleProfileActive(p)">
                {{ p.is_active === false ? 'Включить' : 'Отключить' }}
              </button>
              <button v-if="!p.is_builtin" class="btn danger" :disabled="profileSaving" @click="removeProfile(p)">
                Удалить
              </button>
            </div>
          </li>
        </ul>
      </section>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  fetchAiSettings,
  updateAiSetting,
  testAiProvider,
  fetchProfiles,
  createProfile,
  patchProfile,
  deleteProfile,
} from '@/backend/aiApi';

const PROVIDER_FIELDS = [
  'base_url',
  'model',
  'temperature',
  'max_tokens',
  'http_proxy',
  'request_timeout_sec',
];

const TOOLSETS = [
  { id: 'analytics', label: 'Аналитика (SQL, графики, отчёты)' },
  { id: 'memory', label: 'Память (база знаний, заметки)' },
  { id: 'diag', label: 'Диагностика (рейсы, TMS, ATI — read-only)' },
];

function emptyProfileForm() {
  return {
    id: null,
    name: '',
    description: '',
    prompt: '',
    toolsets: ['analytics', 'memory'],
    is_builtin: false,
    is_active: true,
  };
}

export default {
  name: 'AiSettingsPage',
  data() {
    return {
      loading: true,
      loadError: '',
      activeTab: 'provider',
      groups: { provider: {}, prompts: {}, features: {} },
      forms: {
        provider: {
          base_url: '',
          model: '',
          temperature: 0.1,
          max_tokens: 4000,
          http_proxy: '',
          request_timeout_sec: 120,
          api_key: '',
        },
        prompts: { system_prompt: '' },
        features: {
          enabled: true,
          reports_enabled: true,
          execute_sql_enabled: false,
          memory_enabled: true,
          diag_enabled: false,
        },
      },
      apiKeySet: false,
      apiKeyMasked: '',
      saving: '',
      testing: false,
      testResult: null,
      actionNotice: '',
      actionError: '',
      profiles: [],
      profilesLoaded: false,
      profileSaving: false,
      profileForm: null,
      toolsets: TOOLSETS,
    };
  },
  computed: {
    apiKeyHint() {
      if (this.forms.provider.api_key === '__clear__') return '· будет сброшен к значению из окружения';
      if (this.apiKeySet) return `· задан (${this.apiKeyMasked})`;
      return '· берётся из окружения';
    },
    canTest() {
      return Boolean(this.groups.provider?.data?.api_key || this.apiKeySet || this.forms.provider.api_key);
    },
    visibleTabs() {
      return [
        { id: 'provider', label: 'Провайдер' },
        { id: 'prompts', label: 'Промпт' },
        { id: 'features', label: 'Возможности' },
        { id: 'profiles', label: 'Профили' },
      ];
    },
  },
  async created() {
    await this.reload();
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.actionNotice = '';
      this.actionError = '';
      if (tab === 'profiles' && !this.profilesLoaded) {
        this.loadProfiles();
      }
    },
    async loadProfiles() {
      this.actionError = '';
      try {
        const data = await fetchProfiles();
        const list = Array.isArray(data) ? data : data.items || [];
        this.profiles = list.slice().sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100));
        this.profilesLoaded = true;
      } catch (e) {
        this.actionError = `Не удалось загрузить профили: ${e?.response?.data?.detail || e.message || e}`;
      }
    },
    startCreateProfile() {
      this.profileForm = emptyProfileForm();
      this.actionNotice = '';
      this.actionError = '';
    },
    startEditProfile(profile) {
      this.profileForm = {
        id: profile.id,
        name: profile.name || '',
        description: profile.description || '',
        prompt: profile.prompt || '',
        toolsets: Array.isArray(profile.toolsets) ? profile.toolsets.slice() : [],
        is_builtin: Boolean(profile.is_builtin),
        is_active: profile.is_active !== false,
      };
      this.actionNotice = '';
      this.actionError = '';
    },
    cancelProfileEdit() {
      this.profileForm = null;
    },
    toggleProfileToolset(id) {
      if (!this.profileForm || this.profileForm.is_builtin) return;
      const idx = this.profileForm.toolsets.indexOf(id);
      if (idx >= 0) this.profileForm.toolsets.splice(idx, 1);
      else this.profileForm.toolsets.push(id);
    },
    toolsetLabel(id) {
      const found = TOOLSETS.find((t) => t.id === id);
      return found ? found.label : id;
    },
    async saveProfile() {
      const form = this.profileForm;
      if (!form) return;
      if (!form.name.trim()) {
        this.actionError = 'Укажите название профиля';
        return;
      }
      if (!form.toolsets.length) {
        this.actionError = 'Выберите хотя бы один набор инструментов';
        return;
      }
      this.profileSaving = true;
      this.actionError = '';
      this.actionNotice = '';
      try {
        if (form.id) {
          const patch = { description: form.description, prompt: form.prompt };
          if (!form.is_builtin) patch.name = form.name.trim();
          await patchProfile(form.id, patch);
          this.actionNotice = 'Профиль обновлён';
        } else {
          await createProfile({
            name: form.name.trim(),
            description: form.description,
            prompt: form.prompt,
            toolsets: form.toolsets,
          });
          this.actionNotice = 'Профиль создан';
        }
        this.profileForm = null;
        await this.loadProfiles();
      } catch (e) {
        const detail = Array.isArray(e?.response?.data?.detail)
          ? e.response.data.detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
          : (e?.response?.data?.detail || e.message || e);
        this.actionError = `Не сохранено: ${detail}`;
      } finally {
        this.profileSaving = false;
      }
    },
    async toggleProfileActive(profile) {
      this.profileSaving = true;
      this.actionError = '';
      this.actionNotice = '';
      try {
        await patchProfile(profile.id, { is_active: !profile.is_active });
        await this.loadProfiles();
        this.actionNotice = profile.is_active ? 'Профиль отключён' : 'Профиль включён';
      } catch (e) {
        this.actionError = `Не удалось изменить: ${e?.response?.data?.detail || e.message || e}`;
      } finally {
        this.profileSaving = false;
      }
    },
    async removeProfile(profile) {
      const message = profile.is_builtin
        ? `«${profile.name}» — встроенный профиль. Отключить его?`
        : `Удалить профиль «${profile.name}»? Чаты с ним перейдут на профиль по умолчанию.`;
      if (!window.confirm(message)) return;
      this.profileSaving = true;
      this.actionError = '';
      this.actionNotice = '';
      try {
        await deleteProfile(profile.id);
        await this.loadProfiles();
        this.actionNotice = profile.is_builtin ? 'Профиль отключён' : 'Профиль удалён';
      } catch (e) {
        this.actionError = `Не удалось удалить: ${e?.response?.data?.detail || e.message || e}`;
      } finally {
        this.profileSaving = false;
      }
    },
    async reload() {
      this.loading = true;
      this.loadError = '';
      try {
        const data = await fetchAiSettings();
        this.groups = data;
        this.applyToForms(data);
      } catch (e) {
        this.loadError = `Не удалось загрузить настройки: ${e.message || e}`;
      } finally {
        this.loading = false;
      }
    },
    applyToForms(data) {
      const provider = data.provider?.data || {};
      PROVIDER_FIELDS.forEach((field) => {
        if (provider[field] !== undefined && provider[field] !== null) {
          this.forms.provider[field] = provider[field];
        }
      });
      this.apiKeySet = Boolean(provider.api_key_set);
      this.apiKeyMasked = provider.api_key || '';
      this.forms.provider.api_key = '';

      const prompts = data.prompts?.data || {};
      this.forms.prompts.system_prompt = prompts.system_prompt || '';

      const features = data.features?.data || {};
      if (features.enabled !== undefined) this.forms.features.enabled = Boolean(features.enabled);
      if (features.reports_enabled !== undefined) {
        this.forms.features.reports_enabled = Boolean(features.reports_enabled);
      }
      if (features.execute_sql_enabled !== undefined) {
        this.forms.features.execute_sql_enabled = Boolean(features.execute_sql_enabled);
      }
      if (features.memory_enabled !== undefined) {
        this.forms.features.memory_enabled = Boolean(features.memory_enabled);
      }
      if (features.diag_enabled !== undefined) {
        this.forms.features.diag_enabled = Boolean(features.diag_enabled);
      }
    },
    clearApiKey() {
      this.forms.provider.api_key = this.forms.provider.api_key === '__clear__' ? '' : '__clear__';
    },
    buildPatch(group) {
      const patch = {};
      if (group === 'provider') {
        PROVIDER_FIELDS.forEach((field) => {
          const value = this.forms.provider[field];
          const initial = this.groups.provider?.data?.[field];
          if (String(value ?? '') !== String(initial ?? '')) patch[field] = value;
        });
        const key = this.forms.provider.api_key;
        if (key === '__clear__') patch.api_key = '__clear__';
        else if (key) patch.api_key = key;
      } else if (group === 'prompts') {
        const value = this.forms.prompts.system_prompt;
        const initial = this.groups.prompts?.data?.system_prompt || '';
        if (value !== initial) patch.system_prompt = value;
      } else if (group === 'features') {
        Object.keys(this.forms.features).forEach((field) => {
          const value = this.forms.features[field];
          const initial = Boolean(this.groups.features?.data?.[field]);
          if (Boolean(value) !== initial) patch[field] = value;
        });
      }
      return patch;
    },
    async saveGroup(group) {
      const patch = this.buildPatch(group);
      this.actionError = '';
      this.actionNotice = '';
      if (!Object.keys(patch).length) {
        this.actionNotice = 'Нет изменений для сохранения';
        return;
      }
      this.saving = group;
      try {
        const result = await updateAiSetting(group, patch);
        this.groups[group] = result;
        this.applyToForms({ ...this.groups, [group]: result });
        this.actionNotice = `Группа «${group}» сохранена`;
        if (group === 'provider') this.testResult = null;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.actionError = detail
          ? `Не сохранено: ${detail}`
          : `Не сохранено: ${e.message || e}`;
      } finally {
        this.saving = '';
      }
    },
    async testProvider() {
      this.testing = true;
      this.testResult = null;
      this.actionError = '';
      try {
        const result = await testAiProvider();
        this.testResult = { ok: true, ...result };
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.testResult = { ok: false, error: detail || e.message || String(e) };
      } finally {
        this.testing = false;
      }
    },
  },
};
</script>

<style scoped>
.ai-settings-page {
  padding: 16px 20px 40px;
}

.toolbar {
  margin-bottom: 16px;
}

.title-block h1 {
  margin: 0 0 4px;
  font-size: 22px;
}

.subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
}

.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.settings-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.tabs-side {
  flex-direction: column;
  border-bottom: none;
  border-right: 1px solid #e5e7eb;
  margin-bottom: 0;
  padding-right: 4px;
  min-width: 170px;
  position: sticky;
  top: 16px;
}

.settings-col {
  flex: 1;
  min-width: 0;
}

.tab {
  border: none;
  background: none;
  padding: 8px 16px;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  text-align: left;
  white-space: nowrap;
}

.tab:hover {
  color: #374151;
}

.tab.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
  font-weight: 600;
}

.hint,
.notice,
.error,
.test-result {
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 12px;
}

.hint {
  color: #6b7280;
}

.notice {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.test-result.ok {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
  margin-top: 8px;
}

.test-result.fail {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  margin-top: 8px;
}

.card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0 0 4px;
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.card-header h2 {
  margin: 0;
  font-size: 16px;
}

.badge {
  font-size: 11px;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 1px 8px;
}

.header-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

.btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover:not(:disabled) {
  background: #f9fafb;
}

.btn.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.fields {
  display: grid;
  gap: 12px;
  margin-bottom: 4px;
}

.fields.two-col {
  grid-template-columns: 1fr 1fr;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 12px;
  color: #4b5563;
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.field-hint {
  font-size: 11px;
  color: #9ca3af;
}

.field input[type='text'],
.field input[type='number'],
.field input[type='password'],
.field textarea {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
}

.field textarea {
  font-family: 'Segoe UI', Roboto, sans-serif;
  line-height: 1.5;
  resize: vertical;
}

.api-key-row {
  display: flex;
  gap: 8px;
}

.api-key-row input {
  flex: 1;
}

.switches {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

.switch-row input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.badge-off {
  color: #6b7280;
  background: #f3f4f6;
  border-color: #e5e7eb;
}

.btn.danger {
  color: #b91c1c;
  border-color: #fecaca;
}

.btn.danger:hover:not(:disabled) {
  background: #fef2f2;
}

.profile-editor {
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-editor-actions {
  display: flex;
  gap: 8px;
}

.profile-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.profile-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
}

.profile-item.inactive {
  opacity: 0.6;
}

.profile-item-main {
  min-width: 0;
  flex: 1;
}

.profile-item-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 14px;
}

.profile-item-desc {
  color: #6b7280;
  font-size: 12px;
  margin-top: 2px;
}

.profile-item-tools {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.tool-chip {
  font-size: 11px;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 1px 8px;
}

.profile-item-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
</style>
